<script setup>
/* global axios */
import {
  computed,
  onBeforeUnmount,
  onMounted,
  nextTick,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ConversationApi from 'dashboard/api/inbox/conversation';
import MessageApi from 'dashboard/api/inbox/message';
import KanbanSettingsAPI from 'dashboard/api/kanbanSettings';
import LabelsAPI from 'dashboard/api/labels';
import TalkviaInventoryMovementsAPI from 'dashboard/api/talkviaInventoryMovements';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';

const route = useRoute();
const router = useRouter();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

const statusColumns = [
  {
    key: wootConstants.STATUS_TYPE.OPEN,
    label: 'Abertas',
    icon: 'i-lucide-message-circle',
    type: 'status',
  },
  {
    key: wootConstants.STATUS_TYPE.PENDING,
    label: 'Pendentes',
    icon: 'i-lucide-clock',
    type: 'status',
  },
  {
    key: wootConstants.STATUS_TYPE.RESOLVED,
    label: 'Resolvidas',
    icon: 'i-lucide-check-circle-2',
    type: 'status',
  },
];

const defaultFunnelStages = [
  'Novo lead',
  'Em tratamento',
  'Cancelado',
  'Concluir',
];

const refreshOptions = [
  { value: 0, label: 'Manual' },
  { value: 1, label: '1s' },
  { value: 2, label: '2s' },
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
];

const defaultModels = [
  {
    id: 'status-default',
    name: 'Status padrão',
    type: 'status',
    stages: statusColumns.map(column => column.label),
  },
  {
    id: 'sales-funnel',
    name: 'Funil comercial',
    type: 'funnel',
    stages: defaultFunnelStages,
  },
];

const copy = {
  title: 'Kanban',
  description: 'Organize conversas por funis de atendimento.',
  refresh: 'Atualizar',
  refreshEvery: 'Atualizar a cada',
  kanbanView: 'Kanban',
  funnelView: 'Visão Funil',
  newModel: 'Novo modelo',
  editModel: 'Editar funil',
  modelSelect: 'Modelo do kanban',
  modelName: 'Nome do modelo',
  modelType: 'Tipo do modelo',
  manualStages: 'Etapas manuais',
  labelStages: 'Usar etiquetas',
  stages: 'Etapas do funil',
  stagesHint: 'Arraste os badges para organizar as etapas na ordem do fluxo.',
  stageNamePlaceholder: 'Nome da nova etapa',
  addStage: 'Adicionar etapa',
  stageRules: 'Regras de entrada',
  stageRulesHint:
    'As regras são verificadas ao arrastar o card ou usar os botões de avanço.',
  requireComment: 'Exigir comentário interno',
  requireCommentHint: 'Registra o motivo antes de concluir a movimentação.',
  requireValue: 'Exigir valor informado',
  requireValueHint: 'Impede a entrada quando o card não possui valor.',
  requireAssignee: 'Exigir atendente',
  requireAssigneeHint: 'Impede a entrada em cards sem responsável.',
  autoResolve: 'Resolver conversa automaticamente',
  autoResolveHint: 'Marca a conversa como resolvida ao entrar na etapa.',
  selectStageRules: 'Selecione uma etapa para configurar suas regras.',
  previousStage: 'Voltar',
  nextStage: 'Avançar',
  transitionTitle: 'Confirmar mudança de etapa',
  transitionComment: 'Comentário interno obrigatório',
  transitionCommentPlaceholder: 'Informe o motivo desta movimentação...',
  privateNoteHint: 'O comentário será salvo como nota privada na conversa.',
  confirmTransition: 'Mover conversa',
  removeStage: 'Remover etapa',
  valueRequired: 'Informe um valor antes de mover para esta etapa.',
  assigneeRequired: 'Atribua um atendente antes de mover para esta etapa.',
  commentRequired: 'Informe o comentário obrigatório para continuar.',
  labelsHint: 'Selecione as etiquetas que serão usadas como etapas do kanban.',
  saveModel: 'Salvar modelo',
  saveChanges: 'Salvar alterações',
  cancel: 'Cancelar',
  empty: 'Nenhuma conversa.',
  noMessage: 'Sem mensagem',
  value: 'Valor',
  valuePlaceholder: 'R$ 0,00',
  currencySymbol: 'R$',
  contactMetaSeparator: '·',
  funnelSummary: 'Apresentação do funil',
  funnelPerformance: 'Desempenho do funil',
  funnelPerformanceHint:
    'Compare volume, valor e conversão entre as etapas do fluxo.',
  pipelineValue: 'Valor no funil',
  finalConversion: 'Conversão final',
  ofPreviousStage: 'da etapa anterior',
  entryStage: 'Etapa de entrada',
  ofLargestVolume: 'do maior volume',
  reorderHint: 'Arraste as etapas para reorganizar a ordem do funil.',
  conversations: 'conversas',
  total: 'Total',
  finalStage: 'Etapa final',
  contactModalTitle: 'Detalhes do contato',
  contactInfo: 'Dados do cliente',
  phone: 'Telefone',
  email: 'E-mail',
  assignee: 'Atendente',
  lastActivity: 'Última atividade',
  contactValue: 'Valor da conversa',
  products: 'Produtos do estoque',
  product: 'Produto',
  quantity: 'Quantidade',
  addProduct: 'Cadastrar produto',
  noProducts: 'Nenhum produto cadastrado nesta conversa.',
  message: 'Mensagem para o contato',
  messagePlaceholder: 'Digite a mensagem que será enviada ao contato...',
  sendMessage: 'Enviar mensagem',
  finish: 'Finalizar',
  openConversation: 'Abrir conversa',
  focusedConversation: 'Conversa localizada no Kanban.',
  close: 'Fechar',
  returnStockQuestion:
    'Esta conversa tem produtos baixados do estoque. Deseja retornar as quantidades ao estoque?',
  stockModalTitle: 'Finalizar conversa com produtos',
  returnAndFinish: 'Devolver e finalizar',
  finishWithoutReturn: 'Finalizar sem devolver',
  productAdded: 'Produto cadastrado e baixado do estoque.',
  messageSent: 'Mensagem enviada.',
  finished: 'Conversa finalizada.',
  stockReturned: 'Produtos retornados ao estoque.',
  downloaded: 'Baixado',
  unitFallback: 'un',
  actionFailed: 'Não foi possível concluir a ação.',
  modalTitle: 'Criar modelo de kanban',
  editModalTitle: 'Editar modelo de kanban',
  modalDescription:
    'Defina o fluxo que o administrador quer acompanhar no kanban.',
  editModalDescription:
    'Ajuste as etapas, regras e responsáveis que participam deste funil.',
  audience: 'Quem participa deste funil',
  audienceHint:
    'Sem vínculos, o funil mostra todas as conversas. Com vínculos, entram conversas atribuídas a qualquer agente ou time selecionado.',
  linkedAgents: 'Agentes vinculados',
  linkedTeams: 'Times vinculados',
  selectAgents: 'Selecione um ou mais agentes',
  selectTeams: 'Selecione um ou mais times',
  searchAgents: 'Buscar agente...',
  searchTeams: 'Buscar time...',
  noAgents: 'Nenhum agente encontrado.',
  noTeams: 'Nenhum time encontrado.',
  allAssignments: 'Todas as atribuições',
  defaultStages: defaultFunnelStages.join('\n'),
};

const storageKey = computed(
  () => `talkvia-kanban-models-${route.params.accountId}`
);
const columnOrderStorageKey = computed(
  () => `talkvia-kanban-column-order-${route.params.accountId}`
);
const conversationValuesStorageKey = computed(
  () => `talkvia-kanban-conversation-values-${route.params.accountId}`
);
const columnsData = reactive({});
const savedModels = ref([]);
const columnOrderByModel = ref({});
const conversationValues = ref({});
const conversationInventoryItems = ref({});
const inventoryProducts = ref([]);
const labels = ref([]);
const activeModelId = ref(defaultModels[1].id);
const autoRefreshSeconds = ref(0);
const modelRevision = ref(0);
const activeView = ref('kanban');
const isFetching = ref(false);
const isModelModalOpen = ref(false);
const isTransitionModalOpen = ref(false);
const isStockReturnModalOpen = ref(false);
const collapsedColumns = ref({});
const movingConversationId = ref(null);
const draggedConversation = ref(null);
const draggedColumnKey = ref(null);
const draggedModelStage = ref(null);
const dragOverModelStage = ref(null);
const conversationValueDrafts = ref({});
const selectedConversation = ref(null);
const focusedConversationId = ref(null);
const processedRouteConversationId = ref(null);
const isContactModalOpen = ref(false);
const isModalSaving = ref(false);
const pendingTransition = ref(null);
const transitionComment = ref('');
let autoRefreshTimer;

const modelForm = reactive({
  id: null,
  name: '',
  type: 'manual',
  stages: [...defaultFunnelStages],
  stageDraft: '',
  stageRules: [],
  selectedStage: defaultFunnelStages[0],
  labels: [],
  agentIds: [],
  teamIds: [],
});

const productForm = reactive({
  productId: '',
  quantity: 1,
});

const messageForm = reactive({
  content: '',
});

const allModels = computed(() => {
  const savedModelIds = new Set(savedModels.value.map(model => model.id));
  return [
    ...defaultModels.filter(model => !savedModelIds.has(model.id)),
    ...savedModels.value,
  ];
});

const modelOptions = computed(() =>
  allModels.value.map(model => ({ value: model.id, label: model.name }))
);

const isFunnelView = computed(() => activeView.value === 'funnel');
const isEditingModel = computed(() => !!modelForm.id);

const activeModel = computed(
  () =>
    allModels.value.find(model => model.id === activeModelId.value) ||
    defaultModels[1]
);

const stageRuleFor = (model, stage) =>
  model.stage_rules?.find(rule => rule.stage === stage) || {};

const applyColumnOrder = (model, baseColumns) => {
  const savedOrder = columnOrderByModel.value[model.id];
  if (!savedOrder?.length) return baseColumns;

  const columnByKey = new Map(baseColumns.map(column => [column.key, column]));
  const orderedColumns = savedOrder
    .map(key => columnByKey.get(key))
    .filter(Boolean);
  const missingColumns = baseColumns.filter(
    column => !savedOrder.includes(column.key)
  );

  return [...orderedColumns, ...missingColumns];
};

const columns = computed(() => {
  const baseColumns =
    activeModel.value.type === 'status'
      ? statusColumns
      : activeModel.value.stages.map(stage => ({
          key: stage,
          label: stage,
          icon: 'i-lucide-kanban-square',
          type: 'funnel',
          rules: stageRuleFor(activeModel.value, stage),
        }));

  return applyColumnOrder(activeModel.value, baseColumns);
});

const funnelStageKeys = computed(() =>
  activeModel.value.type !== 'status'
    ? columns.value.map(column => column.key)
    : []
);

const hasConversations = computed(() =>
  columns.value.some(column => columnsData[column.key]?.length)
);

const totalConversations = computed(() =>
  columns.value.reduce(
    (total, column) => total + (columnsData[column.key]?.length || 0),
    0
  )
);

const finalStageCount = computed(() => {
  const finalColumn = columns.value[columns.value.length - 1];
  return finalColumn ? columnsData[finalColumn.key]?.length || 0 : 0;
});

const columnValueTotal = column =>
  (columnsData[column.key] || []).reduce(
    (total, conversation) =>
      total + (conversationValues.value[conversation.id] || 0),
    0
  );

const funnelRows = computed(() => {
  const counts = columns.value.map(
    column => columnsData[column.key]?.length || 0
  );
  const maximumCount = Math.max(...counts, 1);

  return columns.value.map((column, index) => {
    const count = counts[index];
    const previousCount = counts[index - 1];
    return {
      ...column,
      count,
      value: columnValueTotal(column),
      progress: count ? Math.max(4, (count / maximumCount) * 100) : 0,
      conversion:
        index > 0 && previousCount ? (count / previousCount) * 100 : null,
    };
  });
});

const totalPipelineValue = computed(() =>
  funnelRows.value.reduce((total, row) => total + row.value, 0)
);

const finalConversionRate = computed(() => {
  const firstStageCount = funnelRows.value[0]?.count || 0;
  return firstStageCount ? (finalStageCount.value / firstStageCount) * 100 : 0;
});

const formatPercentage = value =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value || 0)}%`;

const valueToneClass = value => {
  if (value > 0) return 'text-n-teal-11';
  if (value < 0) return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

const isColumnCollapsed = column => !!collapsedColumns.value[column.key];

const toggleColumnCollapse = column => {
  collapsedColumns.value = {
    ...collapsedColumns.value,
    [column.key]: !isColumnCollapsed(column),
  };
};

const kanbanGridTemplateColumns = computed(() =>
  columns.value
    .map(column =>
      isColumnCollapsed(column) ? '4.5rem' : 'minmax(18rem, 1fr)'
    )
    .join(' ')
);

const labelOptions = computed(() =>
  labels.value.map(label => ({
    value: label.title,
    label: label.title,
  }))
);

const agentOptions = computed(() =>
  agents.value.map(agent => ({
    value: Number(agent.id),
    label: agent.name || agent.available_name || agent.email,
  }))
);

const teamOptions = computed(() =>
  teams.value.map(team => ({
    value: Number(team.id),
    label: team.name,
  }))
);

const activeModelAssignmentSummary = computed(() => {
  const agentCount = activeModel.value.agent_ids?.length || 0;
  const teamCount = activeModel.value.team_ids?.length || 0;
  if (!agentCount && !teamCount) return copy.allAssignments;

  return [
    agentCount ? `${agentCount} agente${agentCount === 1 ? '' : 's'}` : '',
    teamCount ? `${teamCount} time${teamCount === 1 ? '' : 's'}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
});

const selectedModelStageNames = computed(() =>
  modelForm.type === 'labels' ? modelForm.labels : modelForm.stages
);

const selectedStageRule = computed(() =>
  modelForm.stageRules.find(rule => rule.stage === modelForm.selectedStage)
);

const stageRuleOptions = computed(() => [
  {
    key: 'require_comment',
    label: copy.requireComment,
    hint: copy.requireCommentHint,
    icon: 'i-lucide-message-square-text',
  },
  {
    key: 'require_value',
    label: copy.requireValue,
    hint: copy.requireValueHint,
    icon: 'i-lucide-circle-dollar-sign',
  },
  {
    key: 'require_assignee',
    label: copy.requireAssignee,
    hint: copy.requireAssigneeHint,
    icon: 'i-lucide-user-check',
  },
  {
    key: 'auto_resolve',
    label: copy.autoResolve,
    hint: copy.autoResolveHint,
    icon: 'i-lucide-check-check',
  },
]);

const senderName = conversation =>
  conversation.meta?.sender?.name ||
  conversation.meta?.sender?.phone_number ||
  '-';

const assigneeName = conversation =>
  conversation.meta?.assignee?.name || 'Sem atendente';

const formatDate = timestamp => {
  if (!timestamp) return '-';
  const date =
    typeof timestamp === 'number'
      ? new Date(timestamp * 1000)
      : new Date(timestamp);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatQuantity = value => Number(value || 0).toLocaleString('pt-BR');

const formatCurrencyInput = value =>
  value
    ? new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : '';

const formatPhoneNumber = value => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 13);
  if (!digits) return '-';

  const hasCountryCode = digits.startsWith('55') && digits.length > 11;
  const countryCode = hasCountryCode ? '+55 ' : '';
  const localDigits = hasCountryCode ? digits.slice(2) : digits;
  const areaCode = localDigits.slice(0, 2);
  const number = localDigits.slice(2);
  const splitAt = number.length > 8 ? 5 : 4;
  const firstPart = number.slice(0, splitAt);
  const secondPart = number.slice(splitAt);

  return `${countryCode}(${areaCode}) ${firstPart}${secondPart ? `-${secondPart}` : ''}`;
};

const conversationLabels = conversation =>
  (conversation.labels || [])
    .map(label => (typeof label === 'string' ? label : label.title))
    .filter(Boolean);

const selectedConversationItems = computed(() => {
  if (!selectedConversation.value) return [];
  return conversationInventoryItems.value[selectedConversation.value.id] || [];
});

const selectedConversationReference = computed(() => {
  if (!selectedConversation.value) return '';
  return `#${selectedConversation.value.display_id || selectedConversation.value.id}`;
});

const selectedConversationSender = computed(
  () => selectedConversation.value?.meta?.sender || {}
);

const productById = computed(() =>
  inventoryProducts.value.reduce((result, product) => {
    result[Number(product.id)] = product;
    return result;
  }, {})
);

const activeInventoryProducts = computed(() =>
  inventoryProducts.value.filter(product => product.active !== false)
);

const selectedInventoryProduct = computed(
  () => productById.value[Number(productForm.productId)]
);

const inventoryProductOptionLabel = product =>
  `${product.name} · ${formatQuantity(product.quantity_on_hand)} ${product.unit || copy.unitFallback}`;

const inventoryProductOptions = computed(() =>
  activeInventoryProducts.value.map(product => ({
    value: product.id,
    label: inventoryProductOptionLabel(product),
  }))
);

const inventoryItemStatusLabel = item =>
  item.returned ? copy.stockReturned : copy.downloaded;

const createModelId = name =>
  `${name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}-${Date.now()}`;

const normalizeStageRule = stage => ({
  stage,
  require_comment: false,
  require_value: false,
  require_assignee: false,
  auto_resolve: false,
});

const normalizeSavedModel = model => ({
  ...model,
  stages: model.stages || [],
  agent_ids: (model.agent_ids || []).map(Number),
  team_ids: (model.team_ids || []).map(Number),
  stage_rules: (model.stages || []).map(stage => ({
    ...normalizeStageRule(stage),
    ...(model.stage_rules || []).find(rule => rule.stage === stage),
    stage,
  })),
});

const loadSavedModels = () => {
  try {
    savedModels.value = JSON.parse(
      localStorage.getItem(storageKey.value) || '[]'
    ).map(normalizeSavedModel);
  } catch {
    savedModels.value = [];
  }
};

const loadColumnOrder = () => {
  try {
    columnOrderByModel.value = JSON.parse(
      localStorage.getItem(columnOrderStorageKey.value) || '{}'
    );
  } catch {
    columnOrderByModel.value = {};
  }
};

const loadConversationValues = () => {
  try {
    conversationValues.value = JSON.parse(
      localStorage.getItem(conversationValuesStorageKey.value) || '{}'
    );
  } catch {
    conversationValues.value = {};
  }
};

const getKanbanSettingsPayload = () => ({
  models: savedModels.value,
  column_order: columnOrderByModel.value,
  conversation_values: conversationValues.value,
  conversation_inventory_items: conversationInventoryItems.value,
});

const persistKanbanSettings = async () => {
  await KanbanSettingsAPI.update(getKanbanSettingsPayload());
};

const persistModels = () => {
  persistKanbanSettings();
};

const persistColumnOrder = () => {
  persistKanbanSettings();
};

const persistConversationValues = () => {
  persistKanbanSettings();
};

const migrateLocalKanbanSettings = async () => {
  loadSavedModels();
  loadColumnOrder();
  loadConversationValues();

  if (
    !savedModels.value.length &&
    !Object.keys(columnOrderByModel.value).length &&
    !Object.keys(conversationValues.value).length
  ) {
    return false;
  }

  await persistKanbanSettings();
  return true;
};

const loadKanbanSettings = async ({ migrateLocal = false } = {}) => {
  const { data } = await KanbanSettingsAPI.get();
  savedModels.value = (data.models || []).map(normalizeSavedModel);
  columnOrderByModel.value = data.column_order || {};
  conversationValues.value = data.conversation_values || {};
  conversationInventoryItems.value = data.conversation_inventory_items || {};

  if (
    migrateLocal &&
    !savedModels.value.length &&
    !Object.keys(columnOrderByModel.value).length &&
    !Object.keys(conversationValues.value).length &&
    !Object.keys(conversationInventoryItems.value).length
  ) {
    await migrateLocalKanbanSettings();
  }
};

const conversationValueInput = conversationId => {
  if (
    Object.prototype.hasOwnProperty.call(
      conversationValueDrafts.value,
      conversationId
    )
  ) {
    return conversationValueDrafts.value[conversationId];
  }
  return formatCurrencyInput(conversationValues.value[conversationId]);
};

const updateConversationValueDraft = (conversationId, event) => {
  const digits = event.target.value.replace(/\D/g, '').slice(0, 15);
  const amount = digits ? Number(digits) / 100 : 0;
  const formattedValue = formatCurrencyInput(amount);
  event.target.value = formattedValue;
  conversationValueDrafts.value = {
    ...conversationValueDrafts.value,
    [conversationId]: formattedValue,
  };
  conversationValues.value = {
    ...conversationValues.value,
    [conversationId]: amount,
  };
};

const commitConversationValue = conversationId => {
  if (
    !Object.prototype.hasOwnProperty.call(
      conversationValueDrafts.value,
      conversationId
    )
  )
    return;

  const remainingDrafts = { ...conversationValueDrafts.value };
  delete remainingDrafts[conversationId];
  conversationValueDrafts.value = remainingDrafts;
  persistConversationValues();
};

const conversationMatchesRouteId = (conversation, conversationId) => {
  const normalizedId = String(conversationId || '');
  return (
    String(conversation.id) === normalizedId ||
    String(conversation.display_id || '') === normalizedId
  );
};

const findConversationInColumns = conversationId => {
  if (!conversationId) return null;

  return columns.value.reduce((match, column) => {
    if (match) return match;

    const conversation = (columnsData[column.key] || []).find(item =>
      conversationMatchesRouteId(item, conversationId)
    );
    return conversation ? { column, conversation } : null;
  }, null);
};

const scrollConversationCardIntoView = async conversation => {
  await nextTick();
  const element = document.getElementById(
    `kanban-conversation-${conversation.id}`
  );
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

function openContactModal(conversation) {
  selectedConversation.value = conversation;
  messageForm.content = '';
  productForm.productId = '';
  productForm.quantity = 1;
  isContactModalOpen.value = true;
}

const openRouteConversationInKanban = async () => {
  const conversationId = route.query.conversation_id;
  if (!conversationId) return;
  if (processedRouteConversationId.value === String(conversationId)) return;

  const result = findConversationInColumns(conversationId);
  if (!result) return;

  processedRouteConversationId.value = String(conversationId);
  collapsedColumns.value = {
    ...collapsedColumns.value,
    [result.column.key]: false,
  };
  focusedConversationId.value = result.conversation.id;
  openContactModal(result.conversation);
  useAlert(copy.focusedConversation);
  await scrollConversationCardIntoView(result.conversation);
};

const closeContactModal = () => {
  isContactModalOpen.value = false;
  selectedConversation.value = null;
};

const persistConversationInventoryItems = () => {
  persistKanbanSettings();
};

const setConversationItems = (conversationId, items) => {
  conversationInventoryItems.value = {
    ...conversationInventoryItems.value,
    [conversationId]: items,
  };
  persistConversationInventoryItems();
};

const movementReference = conversation =>
  `kanban-conversation-${conversation.display_id || conversation.id}`;

async function fetchInventoryProducts() {
  const { data } = await TalkviaInventoryProductsAPI.get({ active: true });
  inventoryProducts.value = data.products || [];
}

function removeFromCurrentColumn(conversationId) {
  columns.value.forEach(column => {
    columnsData[column.key] = (columnsData[column.key] || []).filter(
      item => item.id !== conversationId
    );
  });
}

const addInventoryProductToConversation = async () => {
  if (!selectedConversation.value || !selectedInventoryProduct.value) return;

  const quantity = Number(productForm.quantity || 0);
  if (!quantity) return;

  isModalSaving.value = true;
  try {
    await TalkviaInventoryMovementsAPI.create({
      talkvia_inventory_product_id: selectedInventoryProduct.value.id,
      movement_type: 'outbound',
      quantity,
      unit_cost: selectedInventoryProduct.value.cost_price || 0,
      reference: movementReference(selectedConversation.value),
      notes: `Baixa pelo Kanban - ${senderName(selectedConversation.value)}`,
      metadata: {
        source: 'kanban',
        conversation_id: selectedConversation.value.id,
      },
    });

    const item = {
      id: `${selectedInventoryProduct.value.id}-${Date.now()}`,
      product_id: selectedInventoryProduct.value.id,
      product_name: selectedInventoryProduct.value.name,
      quantity,
      unit: selectedInventoryProduct.value.unit || copy.unitFallback,
      returned: false,
      created_at: new Date().toISOString(),
    };
    setConversationItems(selectedConversation.value.id, [
      item,
      ...selectedConversationItems.value,
    ]);
    productForm.productId = '';
    productForm.quantity = 1;
    useAlert(copy.productAdded);
    await fetchInventoryProducts();
  } catch {
    useAlert(copy.actionFailed);
  } finally {
    isModalSaving.value = false;
  }
};

const sendMessageToContact = async () => {
  if (!selectedConversation.value || !messageForm.content.trim()) return;

  isModalSaving.value = true;
  try {
    await MessageApi.create({
      conversationId: selectedConversation.value.id,
      message: messageForm.content.trim(),
      private: false,
    });
    messageForm.content = '';
    useAlert(copy.messageSent);
  } catch {
    useAlert(copy.actionFailed);
  } finally {
    isModalSaving.value = false;
  }
};

const returnConversationProductsToStock = async conversation => {
  const pendingItems = (
    conversationInventoryItems.value[conversation.id] || []
  ).filter(item => !item.returned);
  if (!pendingItems.length) return;

  await Promise.all(
    pendingItems.map(item =>
      TalkviaInventoryMovementsAPI.create({
        talkvia_inventory_product_id: item.product_id,
        movement_type: 'inbound',
        quantity: item.quantity,
        reference: movementReference(conversation),
        notes: `Retorno pelo Kanban - ${senderName(conversation)}`,
        metadata: {
          source: 'kanban_return',
          conversation_id: conversation.id,
          kanban_item_id: item.id,
        },
      })
    )
  );

  setConversationItems(
    conversation.id,
    (conversationInventoryItems.value[conversation.id] || []).map(item => ({
      ...item,
      returned: true,
      returned_at: item.returned ? item.returned_at : new Date().toISOString(),
    }))
  );
};

const completeSelectedConversation = async returnStock => {
  const conversation = selectedConversation.value;
  if (!conversation) return;

  isModalSaving.value = true;
  try {
    if (returnStock) {
      await returnConversationProductsToStock(conversation);
      useAlert(copy.stockReturned);
      await fetchInventoryProducts();
    }

    await ConversationApi.toggleStatus({
      conversationId: conversation.id,
      status: wootConstants.STATUS_TYPE.RESOLVED,
    });
    removeFromCurrentColumn(conversation.id);
    useAlert(copy.finished);
    isStockReturnModalOpen.value = false;
    closeContactModal();
  } catch {
    useAlert(copy.actionFailed);
  } finally {
    isModalSaving.value = false;
  }
};

const finishSelectedConversation = () => {
  const hasPendingProducts = selectedConversationItems.value.some(
    item => !item.returned
  );
  if (hasPendingProducts) {
    isStockReturnModalOpen.value = true;
    return;
  }

  completeSelectedConversation(false);
};

const openModelModal = () => {
  modelForm.id = null;
  modelForm.name = '';
  modelForm.type = 'manual';
  modelForm.stages = [...defaultFunnelStages];
  modelForm.stageDraft = '';
  modelForm.stageRules = defaultFunnelStages.map(normalizeStageRule);
  modelForm.selectedStage = defaultFunnelStages[0];
  modelForm.labels = [];
  modelForm.agentIds = [];
  modelForm.teamIds = [];
  isModelModalOpen.value = true;
};

const openEditModelModal = () => {
  if (activeModel.value.type === 'status') return;

  const model = normalizeSavedModel(activeModel.value);
  modelForm.id = model.id;
  modelForm.name = model.name;
  modelForm.type = model.type === 'labels' ? 'labels' : 'manual';
  modelForm.stages = [...model.stages];
  modelForm.stageDraft = '';
  modelForm.stageRules = model.stage_rules.map(rule => ({ ...rule }));
  modelForm.selectedStage = model.stages[0] || '';
  modelForm.labels = model.type === 'labels' ? [...model.stages] : [];
  modelForm.agentIds = [...model.agent_ids];
  modelForm.teamIds = [...model.team_ids];
  isModelModalOpen.value = true;
};

const closeModelModal = () => {
  isModelModalOpen.value = false;
};

const ensureModelStageRule = stage => {
  if (!stage) return;
  if (!modelForm.stageRules.some(rule => rule.stage === stage)) {
    modelForm.stageRules.push(normalizeStageRule(stage));
  }
};

const addModelStage = () => {
  const stage = modelForm.stageDraft.trim();
  if (!stage || modelForm.stages.includes(stage)) return;

  modelForm.stages.push(stage);
  modelForm.stageDraft = '';
  ensureModelStageRule(stage);
  modelForm.selectedStage = stage;
};

const removeModelStage = stage => {
  modelForm.stages = modelForm.stages.filter(item => item !== stage);
  modelForm.stageRules = modelForm.stageRules.filter(
    rule => rule.stage !== stage
  );
  if (modelForm.selectedStage === stage) {
    modelForm.selectedStage = modelForm.stages[0] || '';
  }
};

const resetModelStageDrag = () => {
  draggedModelStage.value = null;
  dragOverModelStage.value = null;
};

const handleModelStageDragStart = (stage, event) => {
  draggedModelStage.value = stage;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', stage);
};

const handleModelStageDragOver = stage => {
  if (!draggedModelStage.value || draggedModelStage.value === stage) return;
  dragOverModelStage.value = stage;
};

const handleModelStageDrop = targetStage => {
  const sourceStage = draggedModelStage.value;
  if (!sourceStage || sourceStage === targetStage) {
    resetModelStageDrag();
    return;
  }

  const reorderedStages = [...modelForm.stages];
  const sourceIndex = reorderedStages.indexOf(sourceStage);
  const targetIndex = reorderedStages.indexOf(targetStage);
  if (sourceIndex < 0 || targetIndex < 0) {
    resetModelStageDrag();
    return;
  }

  const [movedStage] = reorderedStages.splice(sourceIndex, 1);
  reorderedStages.splice(targetIndex, 0, movedStage);
  modelForm.stages = reorderedStages;
  modelForm.selectedStage = movedStage;
  resetModelStageDrag();
};

const selectModelStage = stage => {
  ensureModelStageRule(stage);
  modelForm.selectedStage = stage;
};

const saveModel = () => {
  const name = modelForm.name.trim();
  const stages =
    modelForm.type === 'labels' ? modelForm.labels : modelForm.stages;

  if (!name || !stages.length) return;

  const model = {
    id: modelForm.id || createModelId(name),
    name,
    type: modelForm.type === 'labels' ? 'labels' : 'funnel',
    stages,
    stage_rules: stages.map(stage => ({
      ...normalizeStageRule(stage),
      ...modelForm.stageRules.find(rule => rule.stage === stage),
      stage,
    })),
    agent_ids: [...modelForm.agentIds],
    team_ids: [...modelForm.teamIds],
  };

  savedModels.value = [
    model,
    ...savedModels.value.filter(item => item.id !== model.id),
  ];
  activeModelId.value = model.id;
  persistModels();
  closeModelModal();
  modelRevision.value += 1;
};

const fetchConversationPayload = async params => {
  const { data } = await ConversationApi.get(params);
  return data.data?.payload || [];
};

const conversationMatchesActiveModelScope = conversation => {
  const agentIds = activeModel.value.agent_ids || [];
  const teamIds = activeModel.value.team_ids || [];
  if (!agentIds.length && !teamIds.length) return true;

  const assigneeId = Number(conversation.meta?.assignee?.id);
  const teamId = Number(conversation.meta?.team?.id);
  return agentIds.includes(assigneeId) || teamIds.includes(teamId);
};

const fetchColumn = async (column, index) => {
  const params = {
    status: column.type === 'status' ? column.key : 'all',
    assigneeType: wootConstants.ASSIGNEE_TYPE.ALL,
    page: 1,
    sortBy: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  };

  if (column.type === 'status') {
    columnsData[column.key] = (await fetchConversationPayload(params)).filter(
      conversationMatchesActiveModelScope
    );
    return;
  }

  if (index > 0) params.labels = [column.key];
  const conversations = (await fetchConversationPayload(params)).filter(
    conversationMatchesActiveModelScope
  );

  columnsData[column.key] =
    index === 0
      ? conversations.filter(conversation => {
          const currentLabels = conversationLabels(conversation);
          return (
            currentLabels.includes(column.key) ||
            !currentLabels.some(label => funnelStageKeys.value.includes(label))
          );
        })
      : conversations;
};

async function fetchKanban() {
  if (isFetching.value) return;

  isFetching.value = true;
  try {
    await Promise.all(
      columns.value.map((column, index) => fetchColumn(column, index))
    );
    await openRouteConversationInKanban();
  } finally {
    isFetching.value = false;
  }
}

const fetchLabels = async () => {
  const { data } = await LabelsAPI.get();
  labels.value = data.payload || data || [];
};

const clearAutoRefresh = () => {
  if (!autoRefreshTimer) return;

  window.clearInterval(autoRefreshTimer);
  autoRefreshTimer = null;
};

const setupAutoRefresh = () => {
  clearAutoRefresh();
  if (!autoRefreshSeconds.value) return;

  autoRefreshTimer = window.setInterval(
    fetchKanban,
    autoRefreshSeconds.value * 1000
  );
};

const toggleModelLabel = label => {
  modelForm.labels = modelForm.labels.includes(label)
    ? modelForm.labels.filter(item => item !== label)
    : [...modelForm.labels, label];

  if (modelForm.labels.includes(label)) {
    ensureModelStageRule(label);
    modelForm.selectedStage = label;
  } else {
    modelForm.stageRules = modelForm.stageRules.filter(
      rule => rule.stage !== label
    );
    if (modelForm.selectedStage === label) {
      modelForm.selectedStage = modelForm.labels[0] || '';
    }
  }
};

const handleColumnDragStart = column => {
  draggedColumnKey.value = column.key;
};

const handleColumnDrop = targetColumn => {
  const sourceKey = draggedColumnKey.value;
  draggedColumnKey.value = null;
  if (!sourceKey || sourceKey === targetColumn.key) return;

  const currentColumns = columns.value;
  const sourceIndex = currentColumns.findIndex(
    column => column.key === sourceKey
  );
  const targetIndex = currentColumns.findIndex(
    column => column.key === targetColumn.key
  );

  if (sourceIndex < 0 || targetIndex < 0) return;

  const reorderedColumns = [...currentColumns];
  const [movedColumn] = reorderedColumns.splice(sourceIndex, 1);
  reorderedColumns.splice(targetIndex, 0, movedColumn);

  columnOrderByModel.value = {
    ...columnOrderByModel.value,
    [activeModel.value.id]: reorderedColumns.map(column => column.key),
  };
  persistColumnOrder();
};

const closeTransitionModal = () => {
  isTransitionModalOpen.value = false;
  pendingTransition.value = null;
  transitionComment.value = '';
};

const performConversationMove = async (
  conversation,
  targetColumn,
  { comment = '' } = {}
) => {
  if (!conversation) return;
  if (
    targetColumn.type === 'status' &&
    conversation.status === targetColumn.key
  )
    return;

  movingConversationId.value = conversation.id;
  try {
    if (comment.trim()) {
      await MessageApi.create({
        conversationId: conversation.id,
        message: `Movimentação para “${targetColumn.label}”: ${comment.trim()}`,
        private: true,
      });
    }

    if (targetColumn.type === 'status') {
      await ConversationApi.toggleStatus({
        conversationId: conversation.id,
        status: targetColumn.key,
      });
    } else {
      const labelsToKeep = conversationLabels(conversation).filter(
        label => !funnelStageKeys.value.includes(label)
      );
      await axios.post(`${ConversationApi.url}/${conversation.id}/labels`, {
        labels: [...labelsToKeep, targetColumn.key],
      });
    }

    if (targetColumn.rules?.auto_resolve) {
      await ConversationApi.toggleStatus({
        conversationId: conversation.id,
        status: wootConstants.STATUS_TYPE.RESOLVED,
      });
    }

    removeFromCurrentColumn(conversation.id);
    let updatedStatus = conversation.status;
    if (targetColumn.type === 'status') updatedStatus = targetColumn.key;
    if (targetColumn.rules?.auto_resolve) {
      updatedStatus = wootConstants.STATUS_TYPE.RESOLVED;
    }
    columnsData[targetColumn.key] = [
      {
        ...conversation,
        status: updatedStatus,
        labels:
          targetColumn.type === 'funnel'
            ? [
                ...conversationLabels(conversation).filter(
                  label => !funnelStageKeys.value.includes(label)
                ),
                targetColumn.key,
              ]
            : conversation.labels,
      },
      ...(columnsData[targetColumn.key] || []),
    ];
    closeTransitionModal();
  } catch {
    useAlert(copy.actionFailed);
  } finally {
    movingConversationId.value = null;
  }
};

const requestConversationMove = (conversation, targetColumn) => {
  if (!conversation || !targetColumn) return;
  if (
    (columnsData[targetColumn.key] || []).some(
      item => item.id === conversation.id
    )
  )
    return;
  if (
    targetColumn.rules?.require_value &&
    !(conversationValues.value[conversation.id] > 0)
  ) {
    useAlert(copy.valueRequired);
    return;
  }
  if (targetColumn.rules?.require_assignee && !conversation.meta?.assignee) {
    useAlert(copy.assigneeRequired);
    return;
  }
  if (targetColumn.rules?.require_comment) {
    pendingTransition.value = { conversation, targetColumn };
    transitionComment.value = '';
    isTransitionModalOpen.value = true;
    return;
  }

  performConversationMove(conversation, targetColumn);
};

const moveConversation = targetColumn => {
  const conversation = draggedConversation.value;
  draggedConversation.value = null;
  requestConversationMove(conversation, targetColumn);
};

const adjacentColumn = (column, offset) => {
  const currentIndex = columns.value.findIndex(item => item.key === column.key);
  return columns.value[currentIndex + offset];
};

const moveConversationToAdjacent = (conversation, column, offset) => {
  requestConversationMove(conversation, adjacentColumn(column, offset));
};

const confirmTransition = () => {
  if (!transitionComment.value.trim()) {
    useAlert(copy.commentRequired);
    return;
  }
  if (!pendingTransition.value) return;

  performConversationMove(
    pendingTransition.value.conversation,
    pendingTransition.value.targetColumn,
    { comment: transitionComment.value }
  );
};

const openConversation = conversation => {
  router.push(
    frontendURL(
      `accounts/${route.params.accountId}/conversations/${conversation.id}`
    )
  );
};

watch(activeModelId, fetchKanban);
watch(modelRevision, fetchKanban);
watch(autoRefreshSeconds, setupAutoRefresh);
watch(
  () => route.query.conversation_id,
  () => {
    processedRouteConversationId.value = null;
    openRouteConversationInKanban();
  }
);

const handleRemoteKanbanUpdate = async event => {
  if (Number(event.detail?.account_id) !== Number(route.params.accountId)) {
    return;
  }

  await loadKanbanSettings();
  await fetchKanban();
};

onMounted(async () => {
  await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
  ]);
  await loadKanbanSettings({ migrateLocal: true });
  window.addEventListener('talkvia:kanban-updated', handleRemoteKanbanUpdate);
  await fetchLabels();
  await fetchInventoryProducts();
  await fetchKanban();
});

onBeforeUnmount(() => {
  clearAutoRefresh();
  window.removeEventListener(
    'talkvia:kanban-updated',
    handleRemoteKanbanUpdate
  );
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 bg-n-surface-1 px-6 py-4">
      <div
        class="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
      >
        <div>
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ copy.title }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ copy.description }}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <div
            class="inline-flex h-9 rounded-lg border border-n-weak bg-n-alpha-2 p-1"
          >
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md px-3 text-sm font-medium transition"
              :class="
                activeView === 'kanban'
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeView = 'kanban'"
            >
              <span class="i-lucide-kanban-square size-4" />
              {{ copy.kanbanView }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md px-3 text-sm font-medium transition"
              :class="
                activeView === 'funnel'
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeView = 'funnel'"
            >
              <span class="i-lucide-funnel size-4" />
              {{ copy.funnelView }}
            </button>
          </div>

          <span class="sr-only">
            {{ copy.refreshEvery }}
          </span>
          <Select
            v-model="autoRefreshSeconds"
            :options="refreshOptions"
            class="w-28 [&>select]:!h-9 [&>select]:w-full [&>select]:!bg-n-alpha-2 [&>select]:!py-1.5"
          />
          <button
            type="button"
            class="grid size-9 place-content-center rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-11 transition hover:bg-n-alpha-3 hover:text-n-slate-12 disabled:opacity-50"
            :title="copy.refresh"
            :aria-label="copy.refresh"
            :disabled="isFetching"
            @click="fetchKanban"
          >
            <span
              class="i-lucide-refresh-cw size-4"
              :class="{ 'animate-spin': isFetching }"
            />
          </button>
        </div>
      </div>

      <section class="mt-4 rounded-xl border border-n-weak bg-n-solid-1 p-3">
        <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div class="flex min-w-0 flex-1 items-stretch gap-2">
            <span class="sr-only">
              {{ copy.modelSelect }}
            </span>
            <Select
              v-model="activeModelId"
              :options="modelOptions"
              class="!w-auto min-w-0 flex-1 [&>select]:!h-9 [&>select]:w-full [&>select]:!bg-n-alpha-2 [&>select]:!py-1.5"
            />
            <Button
              v-if="activeModel.type !== 'status'"
              icon="i-lucide-pencil"
              :label="copy.editModel"
              color="slate"
              size="sm"
              class="!h-9 shrink-0"
              @click="openEditModelModal"
            />
            <Button
              icon="i-lucide-plus"
              :label="copy.newModel"
              color="blue"
              size="sm"
              class="!h-9 shrink-0"
              @click="openModelModal"
            />
          </div>
          <div class="flex shrink-0 gap-2 text-xs text-n-slate-11">
            <span class="rounded-lg bg-n-alpha-2 px-3 py-2">
              {{ `${copy.total}: ${totalConversations}` }}
            </span>
            <span class="rounded-lg bg-n-alpha-2 px-3 py-2">
              {{ `${copy.finalStage}: ${finalStageCount}` }}
            </span>
          </div>
        </div>

        <div class="mt-3 flex flex-col gap-2">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="mb-0 text-xs text-n-slate-11">
              {{ `${activeModel.name} · ${copy.reorderHint}` }}
            </p>
            <span
              class="inline-flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
            >
              <span class="i-lucide-users-round size-3.5" />
              {{ activeModelAssignmentSummary }}
            </span>
          </div>
          <div
            class="flex overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1"
          >
            <div
              v-for="(column, index) in columns"
              :key="column.key"
              draggable="true"
              class="funnel-step relative flex h-9 min-w-0 flex-1 cursor-grab items-center gap-2 bg-n-alpha-2 px-4 text-xs transition hover:bg-n-alpha-3 active:cursor-grabbing"
              :class="{
                'bg-n-brand text-white': draggedColumnKey === column.key,
                'funnel-step-first': index === 0,
                'funnel-step-last': index === columns.length - 1,
              }"
              @dragstart="handleColumnDragStart(column)"
              @dragend="draggedColumnKey = null"
              @dragover.prevent
              @drop="handleColumnDrop(column)"
            >
              <span class="min-w-0 flex-1 truncate font-medium">
                {{ column.label }}
              </span>
              <span
                class="rounded bg-white/20 px-1.5 py-0.5 text-[0.625rem] font-semibold tabular-nums"
              >
                {{ columnsData[column.key]?.length || 0 }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </header>

    <main class="flex-1 min-h-0 overflow-x-auto px-6 pb-6">
      <div
        v-if="isFetching && !hasConversations"
        class="flex items-center justify-center h-full"
      >
        <Spinner class="text-n-brand" />
      </div>

      <div
        v-else-if="isFunnelView"
        class="min-h-full min-w-[760px] rounded-xl border border-n-weak bg-n-solid-1 p-6"
      >
        <div class="mx-auto w-full max-w-6xl">
          <header
            class="flex flex-col gap-4 border-b border-n-weak pb-5 xl:flex-row xl:items-end xl:justify-between"
          >
            <div>
              <h2 class="mb-0 text-lg font-medium text-n-slate-12">
                {{ copy.funnelPerformance }}
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                {{ copy.funnelPerformanceHint }}
              </p>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <div class="rounded-lg bg-n-alpha-2 px-4 py-3">
                <p class="mb-0 text-xs text-n-slate-11">{{ copy.total }}</p>
                <p
                  class="mb-0 mt-1 text-lg font-semibold tabular-nums text-n-slate-12"
                >
                  {{ totalConversations.toLocaleString('pt-BR') }}
                </p>
              </div>
              <div class="rounded-lg bg-n-alpha-2 px-4 py-3">
                <p class="mb-0 text-xs text-n-slate-11">
                  {{ copy.pipelineValue }}
                </p>
                <p
                  class="mb-0 mt-1 text-lg font-semibold tabular-nums"
                  :class="valueToneClass(totalPipelineValue)"
                >
                  {{ formatCurrency(totalPipelineValue) }}
                </p>
              </div>
              <div class="rounded-lg bg-n-alpha-2 px-4 py-3">
                <p class="mb-0 text-xs text-n-slate-11">
                  {{ copy.finalConversion }}
                </p>
                <p
                  class="mb-0 mt-1 text-lg font-semibold tabular-nums text-n-teal-11"
                >
                  {{ formatPercentage(finalConversionRate) }}
                </p>
              </div>
            </div>
          </header>

          <div class="mt-5 space-y-3">
            <article
              v-for="(row, index) in funnelRows"
              :key="row.key"
              class="grid items-center gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4 md:grid-cols-[minmax(12rem,0.75fr)_minmax(18rem,1.5fr)_auto]"
            >
              <div class="flex min-w-0 items-center gap-3">
                <span
                  class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-sm font-semibold tabular-nums text-n-slate-11"
                >
                  {{ index + 1 }}
                </span>
                <div class="min-w-0">
                  <p class="mb-0 truncate font-medium text-n-slate-12">
                    {{ row.label }}
                  </p>
                  <p
                    v-if="row.conversion !== null"
                    class="mb-0 mt-1 flex items-center gap-1 text-xs text-n-slate-11"
                  >
                    <span class="i-lucide-move-down-right size-3.5" />
                    {{
                      `${formatPercentage(row.conversion)} ${copy.ofPreviousStage}`
                    }}
                  </p>
                  <p v-else class="mb-0 mt-1 text-xs text-n-slate-11">
                    {{ copy.entryStage }}
                  </p>
                </div>
              </div>

              <div class="min-w-0">
                <div
                  class="h-2 overflow-hidden rounded-full bg-n-alpha-2"
                  role="progressbar"
                  :aria-label="row.label"
                  :aria-valuenow="row.progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div
                    class="h-full rounded-full bg-n-teal-9 transition-[width] duration-300"
                    :style="{ width: `${row.progress}%` }"
                  />
                </div>
                <p class="mb-0 mt-2 text-xs text-n-slate-10">
                  {{
                    `${formatPercentage(row.progress)} ${copy.ofLargestVolume}`
                  }}
                </p>
              </div>

              <div class="flex min-w-52 items-center justify-end gap-6">
                <div class="text-right">
                  <p class="mb-0 text-xs text-n-slate-11">
                    {{ copy.conversations }}
                  </p>
                  <p
                    class="mb-0 mt-1 text-lg font-semibold tabular-nums text-n-slate-12"
                  >
                    {{ row.count.toLocaleString('pt-BR') }}
                  </p>
                </div>
                <div class="min-w-28 text-right">
                  <p class="mb-0 text-xs text-n-slate-11">
                    {{ copy.value }}
                  </p>
                  <p
                    class="mb-0 mt-1 text-sm font-semibold tabular-nums"
                    :class="valueToneClass(row.value)"
                  >
                    {{ formatCurrency(row.value) }}
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div
        v-else
        class="grid min-w-[900px] gap-4 h-full"
        :style="{
          gridTemplateColumns: kanbanGridTemplateColumns,
        }"
      >
        <section
          v-for="column in columns"
          :key="column.key"
          class="flex min-h-0 flex-col rounded-lg border border-n-weak bg-n-solid-1 transition-[width]"
          @dragover.prevent
          @drop="moveConversation(column)"
        >
          <header
            class="flex items-start justify-between gap-3 border-b border-n-weak px-3 py-3"
          >
            <div
              class="min-w-0"
              :class="{
                'flex h-full flex-1 items-center justify-center':
                  isColumnCollapsed(column),
              }"
            >
              <div class="flex min-w-0 items-center gap-2 text-n-slate-12">
                <span class="size-4 flex-shrink-0" :class="column.icon" />
                <span
                  class="truncate font-medium"
                  :class="{ hidden: isColumnCollapsed(column) }"
                >
                  {{ column.label }}
                </span>
              </div>
              <p
                v-if="!isColumnCollapsed(column)"
                class="mb-0 mt-1 text-xs font-semibold"
                :class="valueToneClass(columnValueTotal(column))"
              >
                {{ formatCurrency(columnValueTotal(column)) }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <span
                v-if="!isColumnCollapsed(column)"
                class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs"
              >
                {{ columnsData[column.key]?.length || 0 }}
              </span>
              <button
                type="button"
                class="grid size-7 place-content-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :title="
                  isColumnCollapsed(column)
                    ? 'Expandir coluna'
                    : 'Recolher coluna'
                "
                @click.stop="toggleColumnCollapse(column)"
              >
                <span
                  class="size-4"
                  :class="
                    isColumnCollapsed(column)
                      ? 'i-lucide-panel-right-open'
                      : 'i-lucide-panel-right-close'
                  "
                />
              </button>
            </div>
          </header>

          <button
            v-if="isColumnCollapsed(column)"
            type="button"
            class="flex flex-1 flex-col items-center gap-3 px-2 py-4 text-n-slate-11 hover:bg-n-alpha-1"
            @click="toggleColumnCollapse(column)"
          >
            <span
              class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium"
            >
              {{ columnsData[column.key]?.length || 0 }}
            </span>
            <span
              class="max-h-52 min-w-0 rotate-180 truncate text-sm font-medium [writing-mode:vertical-rl]"
            >
              {{ column.label }}
            </span>
          </button>

          <div v-else class="flex-1 space-y-3 overflow-y-auto p-3">
            <article
              v-for="conversation in columnsData[column.key] || []"
              :id="`kanban-conversation-${conversation.id}`"
              :key="conversation.id"
              draggable="true"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-left transition hover:border-n-brand"
              :class="{
                'opacity-50': movingConversationId === conversation.id,
                '!border-n-brand ring-2 ring-n-brand/30':
                  focusedConversationId === conversation.id,
              }"
              @dragstart="draggedConversation = conversation"
            >
              <button
                type="button"
                class="w-full rounded-md text-left outline-none transition hover:bg-n-alpha-1 focus-visible:ring-2 focus-visible:ring-n-brand"
                @click="openContactModal(conversation)"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="truncate font-medium text-n-slate-12">
                      {{ senderName(conversation) }}
                    </p>
                    <p class="mt-1 truncate text-xs text-n-slate-11">
                      {{
                        conversation.messages?.[0]?.content || copy.noMessage
                      }}
                    </p>
                  </div>
                  <span class="text-xs tabular-nums text-n-slate-10">
                    {{ `#${conversation.display_id || conversation.id}` }}
                  </span>
                </div>
                <div class="mt-3 flex items-center justify-between text-xs">
                  <span class="truncate text-n-slate-11">
                    {{ assigneeName(conversation) }}
                  </span>
                  <span class="text-n-slate-10">
                    {{ formatDate(conversation.last_activity_at) }}
                  </span>
                </div>
              </button>
              <div
                class="mt-3 rounded-lg border border-n-weak bg-n-alpha-1 p-2"
                @click.stop
                @dragstart.stop
              >
                <div class="mb-1.5 flex items-center justify-between gap-2">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ copy.contactValue }}
                  </span>
                  <span
                    class="i-lucide-wallet-cards size-3.5 text-n-slate-10"
                  />
                </div>
                <Input
                  :model-value="conversationValueInput(conversation.id)"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  size="sm"
                  placeholder="0,00"
                  custom-input-class="!pl-9 !text-right !font-medium tabular-nums"
                  @input="updateConversationValueDraft(conversation.id, $event)"
                  @blur="commitConversationValue(conversation.id)"
                  @enter="$event.target.blur()"
                  @click.stop
                  @keydown.stop
                >
                  <template #prefix>
                    <span
                      class="pointer-events-none absolute bottom-0 left-3 z-10 flex h-8 items-center text-xs font-medium text-n-slate-11"
                    >
                      {{ copy.currencySymbol }}
                    </span>
                  </template>
                </Input>
              </div>
              <div
                class="mt-3 grid grid-cols-2 gap-2 border-t border-n-weak pt-3"
                @dragstart.stop
              >
                <button
                  type="button"
                  class="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-n-weak px-2 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40"
                  :disabled="
                    !adjacentColumn(column, -1) ||
                    movingConversationId === conversation.id
                  "
                  @click.stop="
                    moveConversationToAdjacent(conversation, column, -1)
                  "
                >
                  <span class="i-lucide-arrow-left size-3.5" />
                  {{ copy.previousStage }}
                </button>
                <button
                  type="button"
                  class="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-n-brand px-2 text-xs font-medium text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                  :disabled="
                    !adjacentColumn(column, 1) ||
                    movingConversationId === conversation.id
                  "
                  @click.stop="
                    moveConversationToAdjacent(conversation, column, 1)
                  "
                >
                  {{ copy.nextStage }}
                  <span class="i-lucide-arrow-right size-3.5" />
                </button>
              </div>
            </article>

            <div
              v-if="!(columnsData[column.key] || []).length"
              class="rounded-lg border border-dashed border-n-weak px-3 py-8 text-center text-sm text-n-slate-11"
            >
              {{ copy.empty }}
            </div>
          </div>
        </section>
      </div>
    </main>

    <Teleport to="body">
      <div
        v-if="isContactModalOpen && selectedConversation"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm"
        @click.self="closeContactModal"
      >
        <div
          class="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-2xl"
        >
          <header
            class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
          >
            <div class="flex min-w-0 items-center gap-3">
              <span
                class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-teal-3 text-n-teal-11"
              >
                <span class="i-lucide-user-round size-5" />
              </span>
              <div class="min-w-0">
                <p class="mb-0 text-xs font-medium text-n-slate-11">
                  {{ copy.contactModalTitle }}
                </p>
                <h2 class="mb-0 truncate text-lg font-semibold text-n-slate-12">
                  {{ senderName(selectedConversation) }}
                </h2>
                <p class="mb-0 truncate text-xs text-n-slate-10">
                  {{ selectedConversationReference }}
                  {{ copy.contactMetaSeparator }}
                  {{ assigneeName(selectedConversation) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.close"
              @click="closeContactModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div
            class="grid min-h-0 gap-4 overflow-y-auto p-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]"
          >
            <section class="space-y-4">
              <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <p class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ copy.contactInfo }}
                  </p>
                  <span
                    class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
                  >
                    {{ selectedConversationItems.length }}
                    {{ copy.products.toLowerCase() }}
                  </span>
                </div>
                <dl class="grid gap-2 sm:grid-cols-2">
                  <div class="rounded-lg bg-n-alpha-1 px-3 py-2.5">
                    <dt class="text-xs text-n-slate-10">{{ copy.phone }}</dt>
                    <dd
                      class="mb-0 mt-1 truncate text-sm font-medium text-n-slate-12"
                    >
                      {{
                        formatPhoneNumber(
                          selectedConversationSender.phone_number
                        )
                      }}
                    </dd>
                  </div>
                  <div class="rounded-lg bg-n-alpha-1 px-3 py-2.5">
                    <dt class="text-xs text-n-slate-10">{{ copy.email }}</dt>
                    <dd
                      class="mb-0 mt-1 truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ selectedConversationSender.email || '-' }}
                    </dd>
                  </div>
                  <div class="rounded-lg bg-n-alpha-1 px-3 py-2.5">
                    <dt class="text-xs text-n-slate-10">{{ copy.assignee }}</dt>
                    <dd
                      class="mb-0 mt-1 truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ assigneeName(selectedConversation) }}
                    </dd>
                  </div>
                  <div class="rounded-lg bg-n-alpha-1 px-3 py-2.5">
                    <dt class="text-xs text-n-slate-10">
                      {{ copy.lastActivity }}
                    </dt>
                    <dd
                      class="mb-0 mt-1 truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ formatDate(selectedConversation.last_activity_at) }}
                    </dd>
                  </div>
                </dl>

                <div class="mt-3 border-t border-n-weak pt-3">
                  <Input
                    :model-value="
                      conversationValueInput(selectedConversation.id)
                    "
                    :label="copy.contactValue"
                    type="text"
                    inputmode="numeric"
                    autocomplete="off"
                    placeholder="0,00"
                    custom-input-class="!pl-11 !text-right !font-semibold tabular-nums"
                    @input="
                      updateConversationValueDraft(
                        selectedConversation.id,
                        $event
                      )
                    "
                    @blur="commitConversationValue(selectedConversation.id)"
                    @enter="$event.target.blur()"
                  >
                    <template #prefix>
                      <span
                        class="pointer-events-none absolute bottom-0 left-3 z-10 flex h-10 items-center text-sm font-medium text-n-slate-11"
                      >
                        {{ copy.currencySymbol }}
                      </span>
                    </template>
                  </Input>
                </div>
              </div>

              <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
                <TextArea
                  v-model="messageForm.content"
                  :label="copy.message"
                  :placeholder="copy.messagePlaceholder"
                  :max-length="2000"
                  auto-height
                />
                <div class="mt-3 flex justify-end">
                  <Button
                    :label="copy.sendMessage"
                    icon="i-lucide-send"
                    color="blue"
                    size="sm"
                    :is-loading="isModalSaving"
                    :disabled="!messageForm.content.trim() || isModalSaving"
                    @click="sendMessageToContact"
                  />
                </div>
              </div>
            </section>

            <section class="space-y-4">
              <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
                <div class="mb-3 flex items-center gap-2">
                  <span
                    class="i-lucide-package-search size-4 text-n-slate-11"
                  />
                  <p class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ copy.products }}
                  </p>
                </div>
                <div
                  class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_7rem] xl:grid-cols-[minmax(0,1fr)_7rem_auto] xl:items-end"
                >
                  <label class="flex min-w-0 flex-col gap-1">
                    <span class="mb-0.5 text-sm font-medium text-n-slate-12">
                      {{ copy.product }}
                    </span>
                    <Select
                      v-model="productForm.productId"
                      :options="inventoryProductOptions"
                      :placeholder="copy.product"
                      class="!w-full [&>select]:w-full"
                    />
                  </label>
                  <Input
                    v-model="productForm.quantity"
                    :label="copy.quantity"
                    type="number"
                    min="1"
                    step="1"
                  />
                  <div class="flex items-end sm:col-span-2 xl:col-span-1">
                    <Button
                      :label="copy.addProduct"
                      icon="i-lucide-package-plus"
                      color="teal"
                      size="sm"
                      class="h-10 w-full justify-center whitespace-nowrap"
                      :is-loading="isModalSaving"
                      :disabled="
                        !productForm.productId ||
                        !productForm.quantity ||
                        isModalSaving
                      "
                      @click="addInventoryProductToConversation"
                    />
                  </div>
                </div>

                <div class="mt-4 space-y-2">
                  <div
                    v-for="item in selectedConversationItems"
                    :key="item.id"
                    class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm"
                  >
                    <div class="min-w-0">
                      <p class="mb-0 truncate font-medium text-n-slate-12">
                        {{ item.product_name }}
                      </p>
                      <p class="mb-0 text-xs text-n-slate-11">
                        {{ formatQuantity(item.quantity) }} {{ item.unit }}
                      </p>
                    </div>
                    <span
                      class="rounded-md px-2 py-1 text-xs font-medium"
                      :class="
                        item.returned
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-amber-3 text-n-amber-11'
                      "
                    >
                      {{ inventoryItemStatusLabel(item) }}
                    </span>
                  </div>
                  <p
                    v-if="!selectedConversationItems.length"
                    class="mb-0 rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-11"
                  >
                    {{ copy.noProducts }}
                  </p>
                </div>
              </div>
            </section>
          </div>

          <footer
            class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak px-6 py-4"
          >
            <Button
              :label="`${copy.openConversation} ${selectedConversationReference}`"
              icon="i-lucide-external-link"
              color="slate"
              size="sm"
              @click="openConversation(selectedConversation)"
            />
            <div class="flex items-center gap-3">
              <Button
                :label="copy.close"
                color="slate"
                size="sm"
                @click="closeContactModal"
              />
              <Button
                :label="copy.finish"
                icon="i-lucide-check-circle-2"
                color="teal"
                size="sm"
                :is-loading="isModalSaving"
                :disabled="isModalSaving"
                @click="finishSelectedConversation"
              />
            </div>
          </footer>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="isStockReturnModalOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm"
        @click.self="isStockReturnModalOpen = false"
      >
        <div
          class="w-full max-w-lg rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
        >
          <div class="flex items-start gap-3">
            <span
              class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-amber-3 text-n-amber-11"
            >
              <span class="i-lucide-package-open size-5" />
            </span>
            <div>
              <h2 class="mb-0 text-lg font-medium text-n-slate-12">
                {{ copy.stockModalTitle }}
              </h2>
              <p class="mb-0 mt-1 text-sm leading-6 text-n-slate-11">
                {{ copy.returnStockQuestion }}
              </p>
            </div>
          </div>
          <div class="mt-6 flex flex-wrap justify-end gap-3">
            <Button
              type="button"
              :label="copy.cancel"
              color="slate"
              size="sm"
              :disabled="isModalSaving"
              @click="isStockReturnModalOpen = false"
            />
            <Button
              type="button"
              :label="copy.finishWithoutReturn"
              color="slate"
              size="sm"
              :disabled="isModalSaving"
              @click="completeSelectedConversation(false)"
            />
            <Button
              type="button"
              icon="i-lucide-package-check"
              :label="copy.returnAndFinish"
              color="teal"
              size="sm"
              :is-loading="isModalSaving"
              @click="completeSelectedConversation(true)"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="isTransitionModalOpen && pendingTransition"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm"
        @click.self="closeTransitionModal"
      >
        <form
          class="w-full max-w-lg rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
          @submit.prevent="confirmTransition"
        >
          <div class="flex items-start gap-3">
            <span
              class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-amber-3 text-n-amber-11"
            >
              <span class="i-lucide-message-square-text size-5" />
            </span>
            <div>
              <h2 class="mb-0 text-lg font-medium text-n-slate-12">
                {{ copy.transitionTitle }}
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                {{
                  `${senderName(pendingTransition.conversation)} → ${pendingTransition.targetColumn.label}`
                }}
              </p>
            </div>
          </div>
          <label class="mt-5 block text-sm font-medium text-n-slate-12">
            <span>{{ copy.transitionComment }}</span>
            <textarea
              v-model="transitionComment"
              class="mt-2 min-h-28 w-full rounded-lg border border-n-weak bg-n-alpha-2 p-3 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:border-n-brand"
              :placeholder="copy.transitionCommentPlaceholder"
              autofocus
            />
          </label>
          <p class="mb-0 mt-2 text-xs text-n-slate-11">
            {{ copy.privateNoteHint }}
          </p>
          <div class="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              :label="copy.cancel"
              color="slate"
              size="sm"
              :disabled="movingConversationId !== null"
              @click="closeTransitionModal"
            />
            <Button
              type="submit"
              icon="i-lucide-arrow-right"
              :label="copy.confirmTransition"
              color="blue"
              size="sm"
              :is-loading="movingConversationId !== null"
              :disabled="!transitionComment.trim()"
            />
          </div>
        </form>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="isModelModalOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm"
        @click.self="closeModelModal"
      >
        <form
          class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-2xl"
          @submit.prevent="saveModel"
        >
          <header
            class="flex shrink-0 items-start justify-between gap-4 border-b border-n-weak px-6 py-4"
          >
            <div class="flex min-w-0 items-start gap-3">
              <span
                class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-teal-3 text-n-teal-11"
              >
                <span
                  class="size-5"
                  :class="
                    isEditingModel
                      ? 'i-lucide-pencil-ruler'
                      : 'i-lucide-workflow'
                  "
                />
              </span>
              <div>
                <h2 class="mb-0 text-lg font-medium text-n-slate-12">
                  {{ isEditingModel ? copy.editModalTitle : copy.modalTitle }}
                </h2>
                <p class="mb-0 mt-1 text-sm text-n-slate-11">
                  {{
                    isEditingModel
                      ? copy.editModalDescription
                      : copy.modalDescription
                  }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.close"
              @click="closeModelModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <label class="flex flex-col gap-2 text-sm text-n-slate-12">
              <span>{{ copy.modelName }}</span>
              <input
                v-model="modelForm.name"
                class="h-11 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm outline-none focus:border-n-brand"
                :placeholder="copy.modelName"
              />
            </label>

            <div class="mt-5 flex flex-col gap-2 text-sm text-n-slate-12">
              <span>{{ copy.modelType }}</span>
              <div class="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  class="rounded-lg border px-3 py-2 text-sm"
                  :class="
                    modelForm.type === 'manual'
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-weak bg-n-alpha-2 text-n-slate-12'
                  "
                  @click="modelForm.type = 'manual'"
                >
                  {{ copy.manualStages }}
                </button>
                <button
                  type="button"
                  class="rounded-lg border px-3 py-2 text-sm"
                  :class="
                    modelForm.type === 'labels'
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-weak bg-n-alpha-2 text-n-slate-12'
                  "
                  @click="modelForm.type = 'labels'"
                >
                  {{ copy.labelStages }}
                </button>
              </div>
            </div>

            <section
              class="mt-5 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            >
              <div class="flex items-start gap-3">
                <span
                  class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                >
                  <span class="i-lucide-users-round size-4" />
                </span>
                <div>
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ copy.audience }}
                  </p>
                  <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                    {{ copy.audienceHint }}
                  </p>
                </div>
              </div>
              <div class="mt-4 grid gap-4 sm:grid-cols-2">
                <label class="flex min-w-0 flex-col gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ copy.linkedAgents }}
                  </span>
                  <TagMultiSelectComboBox
                    v-model="modelForm.agentIds"
                    :options="agentOptions"
                    :placeholder="copy.selectAgents"
                    :search-placeholder="copy.searchAgents"
                    :empty-state="copy.noAgents"
                  />
                </label>
                <label class="flex min-w-0 flex-col gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ copy.linkedTeams }}
                  </span>
                  <TagMultiSelectComboBox
                    v-model="modelForm.teamIds"
                    :options="teamOptions"
                    :placeholder="copy.selectTeams"
                    :search-placeholder="copy.searchTeams"
                    :empty-state="copy.noTeams"
                  />
                </label>
              </div>
            </section>

            <div
              v-if="modelForm.type === 'manual'"
              class="mt-4 flex flex-col gap-2 text-sm text-n-slate-12"
            >
              <span>{{ copy.stages }}</span>
              <div class="flex gap-2">
                <input
                  v-model="modelForm.stageDraft"
                  class="h-10 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm outline-none focus:border-n-brand"
                  :placeholder="copy.stageNamePlaceholder"
                  @keydown.enter.prevent="addModelStage"
                />
                <Button
                  type="button"
                  icon="i-lucide-plus"
                  :label="copy.addStage"
                  color="blue"
                  size="sm"
                  class="!h-10 shrink-0"
                  :disabled="!modelForm.stageDraft.trim()"
                  @click="addModelStage"
                />
              </div>
              <div class="flex flex-wrap gap-2 rounded-xl bg-n-alpha-1 p-3">
                <div
                  v-for="(stage, index) in modelForm.stages"
                  :key="stage"
                  draggable="true"
                  class="inline-flex h-9 cursor-grab items-center overflow-hidden rounded-full border text-sm transition active:cursor-grabbing"
                  :class="[
                    modelForm.selectedStage === stage
                      ? 'border-n-brand bg-n-alpha-2 text-n-teal-11'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-11',
                    {
                      'opacity-40': draggedModelStage === stage,
                      'ring-2 ring-n-brand/40': dragOverModelStage === stage,
                    },
                  ]"
                  @dragstart="handleModelStageDragStart(stage, $event)"
                  @dragenter.prevent="handleModelStageDragOver(stage)"
                  @dragover.prevent
                  @drop.prevent="handleModelStageDrop(stage)"
                  @dragend="resetModelStageDrag"
                >
                  <span
                    class="grid h-full w-7 shrink-0 place-content-center text-n-slate-10"
                    :title="copy.stagesHint"
                  >
                    <span class="i-lucide-grip-vertical size-3.5" />
                  </span>
                  <button
                    type="button"
                    class="inline-flex h-full items-center gap-2 pr-2 font-medium"
                    @click="selectModelStage(stage)"
                  >
                    <span
                      class="grid size-5 place-content-center rounded-full bg-n-alpha-2 text-[0.625rem] font-semibold"
                    >
                      {{ index + 1 }}
                    </span>
                    {{ stage }}
                  </button>
                  <button
                    type="button"
                    class="grid size-7 place-content-center text-n-slate-10 hover:bg-n-ruby-3 hover:text-n-ruby-11"
                    :title="copy.removeStage"
                    @click="removeModelStage(stage)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
              </div>
              <span class="text-xs text-n-slate-11">{{ copy.stagesHint }}</span>
            </div>

            <div
              v-else
              class="mt-4 flex flex-col gap-2 text-sm text-n-slate-12"
            >
              <span>{{ copy.labelStages }}</span>
              <div class="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                <button
                  v-for="label in labelOptions"
                  :key="label.value"
                  type="button"
                  class="rounded-lg border px-3 py-2 text-sm"
                  :class="
                    modelForm.labels.includes(label.value)
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-weak bg-n-alpha-2 text-n-slate-12'
                  "
                  @click="toggleModelLabel(label.value)"
                >
                  {{ label.label }}
                </button>
              </div>
              <span class="text-xs text-n-slate-11">{{ copy.labelsHint }}</span>
              <div
                v-if="selectedModelStageNames.length"
                class="flex flex-wrap gap-2 pt-2"
              >
                <button
                  v-for="stage in selectedModelStageNames"
                  :key="stage"
                  type="button"
                  class="rounded-full border px-3 py-1.5 text-xs font-medium"
                  :class="
                    modelForm.selectedStage === stage
                      ? 'border-n-brand bg-n-alpha-2 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11'
                  "
                  @click="selectModelStage(stage)"
                >
                  {{ stage }}
                </button>
              </div>
            </div>

            <section class="mt-5 border-t border-n-weak pt-5">
              <div>
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ copy.stageRules }}
                </p>
                <p class="mb-0 mt-1 text-xs text-n-slate-11">
                  {{ copy.stageRulesHint }}
                </p>
              </div>
              <div
                v-if="selectedStageRule"
                class="mt-3 grid gap-3 sm:grid-cols-2"
              >
                <button
                  v-for="rule in stageRuleOptions"
                  :key="rule.key"
                  type="button"
                  class="flex items-start gap-3 rounded-xl border p-4 text-left transition"
                  :class="
                    selectedStageRule[rule.key]
                      ? 'border-n-brand bg-n-alpha-2'
                      : 'border-n-weak bg-n-solid-2 hover:border-n-slate-8'
                  "
                  @click="
                    selectedStageRule[rule.key] = !selectedStageRule[rule.key]
                  "
                >
                  <span
                    class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                    :class="{ 'text-n-teal-11': selectedStageRule[rule.key] }"
                  >
                    <span class="size-4" :class="rule.icon" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{ rule.label }}
                    </span>
                    <span class="mt-1 block text-xs leading-5 text-n-slate-11">
                      {{ rule.hint }}
                    </span>
                  </span>
                  <span
                    class="mt-1 grid size-5 shrink-0 place-content-center rounded border"
                    :class="
                      selectedStageRule[rule.key]
                        ? 'border-n-brand bg-n-brand text-white'
                        : 'border-n-weak text-transparent'
                    "
                  >
                    <span class="i-lucide-check size-3" />
                  </span>
                </button>
              </div>
              <p
                v-else
                class="mb-0 mt-3 rounded-lg border border-dashed border-n-weak px-4 py-6 text-center text-sm text-n-slate-11"
              >
                {{ copy.selectStageRules }}
              </p>
            </section>
          </div>

          <footer
            class="flex shrink-0 justify-end gap-3 border-t border-n-weak px-6 py-4"
          >
            <Button
              type="button"
              :label="copy.cancel"
              color="slate"
              size="sm"
              @click="closeModelModal"
            />
            <Button
              type="submit"
              icon="i-lucide-save"
              :label="isEditingModel ? copy.saveChanges : copy.saveModel"
              color="blue"
              size="sm"
            />
          </footer>
        </form>
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.funnel-step {
  clip-path: polygon(
    0 0,
    calc(100% - 0.75rem) 0,
    100% 50%,
    calc(100% - 0.75rem) 100%,
    0 100%,
    0.75rem 50%
  );
}

.funnel-step-first {
  clip-path: polygon(
    0 0,
    calc(100% - 0.75rem) 0,
    100% 50%,
    calc(100% - 0.75rem) 100%,
    0 100%
  );
}

.funnel-step-last {
  clip-path: polygon(0.75rem 0, 100% 0, 100% 100%, 0.75rem 100%, 0 50%);
}

.funnel-step-first.funnel-step-last {
  clip-path: none;
}
</style>
